import { createContext, type MutableRefObject, type ReactNode,useCallback, useContext, useMemo, useRef } from "react";

import { Router as WouterRouter, type RouterProps } from "wouter";

type AroundNavHandler = NonNullable<RouterProps["aroundNav"]>;

export interface ChecklistLeaveGuardListener {
  shouldBlock: () => boolean;
  onPrompt: (proceed: () => void, target: string) => void;
  bypassNav: MutableRefObject<boolean>;
}

interface ChecklistLeaveGuardContextValue {
  setListener: (listener: ChecklistLeaveGuardListener | null) => void;
}

const ChecklistLeaveGuardContext = createContext<ChecklistLeaveGuardContextValue | null>(null);

export function ChecklistLeaveGuardProvider({ children }: { children: ReactNode }) {
  const listenerRef = useRef<ChecklistLeaveGuardListener | null>(null);

  const setListener = useCallback((listener: ChecklistLeaveGuardListener | null) => {
    listenerRef.current = listener;
  }, []);

  const aroundNav = useMemo<AroundNavHandler>(
    () => (navigate, to, options) => {
      const L = listenerRef.current;
      if (L?.bypassNav.current) {
        L.bypassNav.current = false;
        navigate(to, options);
        return;
      }
      if (L?.shouldBlock()) {
        L.onPrompt(() => navigate(to, options), String(to));
        return;
      }
      navigate(to, options);
    },
    [],
  );

  const value = useMemo(() => ({ setListener }), [setListener]);

  return (
    <ChecklistLeaveGuardContext.Provider value={value}>
      <WouterRouter aroundNav={aroundNav}>{children}</WouterRouter>
    </ChecklistLeaveGuardContext.Provider>
  );
}

export function useChecklistLeaveGuard(): ChecklistLeaveGuardContextValue {
  const ctx = useContext(ChecklistLeaveGuardContext);
  if (!ctx) {
    throw new Error("useChecklistLeaveGuard must be used within ChecklistLeaveGuardProvider");
  }
  return ctx;
}
